import { IDocumentRepository } from '../../domain/repositories/IDocumentRepository';
import { Document } from '../../domain/entities/Document';
import { ConfigManager } from '../../application/config/Config';
import { LoggerFactory } from '../../application/logging/Logger';
import { InternalServerError } from '../../application/errors/AppError';

/**
 * API-based Document Repository Implementation
 * Follows Repository Pattern and Dependency Inversion Principle
 */
export class DocumentRepository implements IDocumentRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();

async addJsonKnowledge(userId: string, jsonData: any): Promise<any> {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(
      `${this.config.baseUrl}/api/Documents/add-json-knowledge`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": this.config.apiKey,
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,
          jsonData
        })
      }
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || "Failed to upload JSON knowledge.");
    }

    return await response.json();
  } catch (err: any) {
    console.error("Error in addJsonKnowledge:", err);
    throw err;
  }
}


  async findById(id: string): Promise<Document | null> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/${id}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new InternalServerError('Failed to fetch document');
      }

      const data = await response.json();
      return Document.fromData(data);
    } catch (error) {
      this.logger.error('Failed to find document by ID', error as Error, { documentId: id });
      throw error;
    }
  }

  async findByUserId(userId: string): Promise<Document[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch user documents');
      }

      const data = await response.json();
      return data.documents;
    } catch (error) {
      this.logger.error('Failed to find documents by user ID', error as Error, { userId });
      throw error;
    }
  }

  async findPublic(): Promise<Document[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/public`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch public documents');
      }

      const data = await response.json();
      return data.documents;
    } catch (error) {
      this.logger.error('Failed to find public documents', error as Error);
      throw error;
    }
  }

  async save(document: Document): Promise<Document> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(document.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to save document');
      }

      const data = await response.json();
      return Document.fromData(data);
    } catch (error) {
      this.logger.error('Failed to save document', error as Error, { documentId: document.id });
      throw error;
    }
  }

  async update(document: Document): Promise<Document> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/${document.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(document.toJSON())
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to update document');
      }

      const data = await response.json();
      return Document.fromData(data);
    } catch (error) {
      this.logger.error('Failed to update document', error as Error, { documentId: document.id });
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/${id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to delete document');
      }
    } catch (error) {
      this.logger.error('Failed to delete document', error as Error, { documentId: id });
      throw error;
    }
  }

  async upload(file: File, userId: string): Promise<Document> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);

      const response = await fetch(`${this.config.baseUrl}/documents/upload`, {
        method: 'POST',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to upload document');
      }

      const data = await response.json();
      return Document.fromData(data);
    } catch (error) {
      this.logger.error('Failed to upload document', error as Error, { userId, fileName: file.name });
      throw error;
    }
  }

  async documentupload(file: File): Promise<{ filePath: string }> {
  try {
    const formData = new FormData();
    formData.append("File", file);

    const response = await fetch(`${this.config.aiBaseUrl}/Documents/upload-file`, {
      method: "PUT",
      headers: {
        "X-Api-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token") || ""}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      this.logger.error("Document upload failed", new Error(text), { fileName: file.name });
      throw new InternalServerError(`Failed to upload document: ${text}`);
    }

    const data = await response.json();
    const filePath = data?.result?.filePath;

    if (!filePath) {
      throw new InternalServerError("Upload succeeded but no file path returned from server.");
    }

    return { filePath };
  } catch (error) {
    this.logger.error("Failed to upload document", error as Error, { fileName: file.name });
    throw error;
  }
}

async saveDocumentUploadedFile(file: File, filePath: string, coachId: string): Promise<Document> {
  try {
    const payload = {
      FileName: file.name,
      FilePath: filePath,
      FileType: `.${file.name.split('.').pop()}`,
      CreatedAt: new Date().toISOString(),
      UploadedAt: new Date().toISOString(),
      CoachId: coachId,
    };

    const response = await fetch(`${this.config.aiBaseUrl}/Documents`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Api-Key": this.config.apiKey,
        "Authorization": `Bearer ${localStorage.getItem("auth_token") || ""}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const text = await response.text();
      this.logger.error("Save document failed", new Error(text), payload);
      throw new InternalServerError(`Failed to save document: ${text}`);
    }

    const data = await response.json();
    const result = data.result;

    return Document.fromData({
      id: result.id,
      user_id: result.coachId,
      name: result.fileName,
      type: result.fileType,
      size: 0,
      url: result.filePath,
      created_at: result.createdAt,
      updated_at: result.uploadedAt,
    });
  } catch (error) {
    this.logger.error("Failed to save document", error as Error, { filePath });
    throw error;
  }
}


async getDocumentsByCoachId(coachId: string, pageNumber = 1, pageSize = 10): Promise<Document[]> {
  try {
    const response = await fetch(
      `${this.config.aiBaseUrl}/Documents?coachId=${coachId}&PageNumber=${pageNumber}&PageSize=${pageSize}`,
      {
        method: "GET",
        headers: {
          "accept": "text/plain",
          "X-Api-Key": this.config.apiKey,
          "Authorization": `Bearer ${localStorage.getItem("auth_token") || ""}`,
        },
      }
    );

    if (!response.ok) {
      const text = await response.text();
      this.logger.error("Failed to fetch documents", new Error(text), { coachId });
      throw new InternalServerError(`Failed to fetch documents: ${text}`);
    }

    const data = await response.json();
    const items = data?.result?.items || [];

    return items.map((item: any) =>
      Document.fromData({
        id: item.id,
        user_id: item.coachId,
        name: item.fileName,
        type: item.fileType,
        size: 0, 
        url: item.filePath,
        created_at: item.createdAt,
        updated_at: item.uploadedAt,
      })
    );
  } catch (error) {
    this.logger.error("Error fetching documents", error as Error, { coachId });
    throw error;
  }
}

async deleteDocument(documentId: string, coachId: string): Promise<void> {
  try {
    const response = await fetch(
      `${this.config.aiBaseUrl}/Documents/${documentId}?coachId=${coachId}`,
      {
        method: "DELETE",
        headers: {
          "X-Api-Key": this.config.apiKey,
          "Authorization": `Bearer ${localStorage.getItem("auth_token") || ""}`,
          "accept": "*/*",
        },
      }
    );

    if (!response.ok) {
      const text = await response.text();
      this.logger.error("Failed to delete document", new Error(text), { documentId, coachId });
      throw new InternalServerError(`Failed to delete document: ${text}`);
    }

    this.logger.info("Document deleted successfully", { documentId, coachId });
  } catch (error) {
    this.logger.error("Error deleting document", error as Error, { documentId, coachId });
    throw error;
  }
}



  async download(id: string): Promise<Blob> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/${id}/download`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to download document');
      }

      return await response.blob();
    } catch (error) {
      this.logger.error('Failed to download document', error as Error, { documentId: id });
      throw error;
    }
  }

  async share(id: string, userId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/${id}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to share document');
      }
    } catch (error) {
      this.logger.error('Failed to share document', error as Error, { documentId: id, userId });
      throw error;
    }
  }

  async unshare(id: string, userId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/${id}/unshare`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ user_id: userId })
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to unshare document');
      }
    } catch (error) {
      this.logger.error('Failed to unshare document', error as Error, { documentId: id, userId });
      throw error;
    }
  }

  async search(userId: string, query: string): Promise<Document[]> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        search: query
      });

      const response = await fetch(`${this.config.baseUrl}/documents/search?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to search documents');
      }

      const data = await response.json();
      return data.documents;
    } catch (error) {
      this.logger.error('Failed to search documents', error as Error, { userId, query });
      throw error;
    }
  }

  async findByType(userId: string, type: string): Promise<Document[]> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        type: type
      });

      const response = await fetch(`${this.config.baseUrl}/documents/type?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch documents by type');
      }

      const data = await response.json();
      return data.documents;
    } catch (error) {
      this.logger.error('Failed to find documents by type', error as Error, { userId, type });
      throw error;
    }
  }

  async findByTags(userId: string, tags: string[]): Promise<Document[]> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        tags: tags.join(',')
      });

      const response = await fetch(`${this.config.baseUrl}/documents/tags?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch documents by tags');
      }

      const data = await response.json();
      return data.documents;
    } catch (error) {
      this.logger.error('Failed to find documents by tags', error as Error, { userId, tags });
      throw error;
    }
  }

  async getStorageUsed(userId: string): Promise<number> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/storage?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch storage used');
      }

      const data = await response.json();
      return data.storage_used || 0;
    } catch (error) {
      this.logger.error('Failed to get storage used', error as Error, { userId });
      throw error;
    }
  }

  async getDocumentCount(userId: string): Promise<number> {
    try {
      const response = await fetch(`${this.config.baseUrl}/documents/count?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch document count');
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      this.logger.error('Failed to get document count', error as Error, { userId });
      throw error;
    }
  }

  async findPaginated(userId: string, page: number, limit: number): Promise<{
    documents: Document[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    try {
      const params = new URLSearchParams({
        user_id: userId,
        page: page.toString(),
        limit: limit.toString()
      });

      const response = await fetch(`${this.config.baseUrl}/documents?${params}`, {
        method: 'GET',
        headers: {
          'X-API-Key': this.config.apiKey,
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new InternalServerError('Failed to fetch paginated documents');
      }

      const data = await response.json();
      return {
        documents: data.documents,
        total: data.total || 0,
        page: data.page || page,
        limit: data.limit || limit,
        totalPages: data.total_pages || 0
      };
    } catch (error) {
      this.logger.error('Failed to get paginated documents', error as Error, { 
        userId, 
        page, 
        limit 
      });
      throw error;
    }
  }
}
